import { forwardRef, useEffect, useId, useState, useSyncExternalStore, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from "react";
import { Check, Loader2, type LucideIcon } from "lucide-react";
import { colorHex, PROJECT_COLORS, type Collaborator } from "@todoer/shared";
import { Illustration, type IllustrationKind } from "./Illustration.js";

/*
 * Design-system primitives. Every screen builds from these so states (hover,
 * pressed, disabled, selected, focus) are defined once. Colours come from the
 * tokens in index.css; never use raw palette colours in components.
 */

export const cx = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(" ");

// ------------------------------------------------------------------ media
/** Phone = a small touch screen. A narrow window on a computer keeps the desktop layout. Keep in step with index.css. */
export const phoneQuery = "(max-width: 767px) and (pointer: coarse)";
/** Desktop windows narrower than this show the sidebar as an icon strip. */
export const compactQuery = "(max-width: 1023px)";
/** Desktop windows narrower than this hide the sidebar behind a menu button. */
export const narrowQuery = "(max-width: 639px)";
function subscribeMedia(query: string) {
  return (cb: () => void) => {
    if (typeof window === "undefined" || !window.matchMedia) return () => {};
    const m = window.matchMedia(query);
    m.addEventListener?.("change", cb);
    return () => m.removeEventListener?.("change", cb);
  };
}
const matches = (query: string) => typeof window !== "undefined" && !!window.matchMedia?.(query)?.matches;

/** True on phones: bottom tabs, sheets, swipe actions. Narrow computer windows are not phones. */
export function useIsPhone(): boolean {
  return useSyncExternalStore(subscribeMedia(phoneQuery), () => matches(phoneQuery), () => false);
}
export const isPhoneNow = () => matches(phoneQuery);
export function useMedia(query: string): boolean {
  return useSyncExternalStore(subscribeMedia(query), () => matches(query), () => false);
}

// ------------------------------------------------------------------ buttons
type Variant = "primary" | "secondary" | "ghost" | "danger" | "outline-brand";
type Size = "sm" | "md" | "lg";

const variants: Record<Variant, string> = {
  primary: "bg-brand-strong text-white hover:brightness-110 active:brightness-95 disabled:opacity-50",
  secondary: "border border-border bg-surface-raised text-text hover:bg-hover active:bg-pressed disabled:opacity-50",
  ghost: "text-text-muted hover:bg-hover hover:text-text active:bg-pressed disabled:opacity-50",
  danger: "bg-danger text-white hover:brightness-110 active:brightness-95 disabled:opacity-50 dark:text-black",
  "outline-brand": "border border-brand/40 text-brand-text hover:bg-brand-soft active:bg-brand-soft disabled:opacity-50",
};
const sizes: Record<Size, string> = {
  sm: "h-7 gap-1.5 px-2.5 text-meta font-medium phone:h-11 phone:px-3",
  md: "h-8 gap-2 px-3 text-body font-medium phone:h-11",
  lg: "h-11 gap-2 px-4 text-body font-semibold",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: LucideIcon;
  iconRight?: LucideIcon;
  /** Tooltip text (shown after 300ms on hover or keyboard focus) */
  tip?: string;
  /** Keyboard shortcut shown in the tooltip */
  shortcut?: string;
  loading?: boolean;
  block?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", size = "md", icon: Icon, iconRight: IconRight, tip, shortcut, loading, block, className, children, type = "button", ...rest }, ref,
) {
  return (
    <button ref={ref} type={type} data-tip={tip} data-tip-key={shortcut}
      className={cx("inline-flex shrink-0 select-none items-center justify-center whitespace-nowrap rounded-control transition-[background-color,filter,color,border-color] duration-150 ease-out",
        variants[variant], sizes[size], block && "w-full", className)}
      {...rest}>
      {loading ? <Loader2 size={16} className="anim-spin" aria-hidden /> : Icon && <Icon size={size === "sm" ? 14 : 16} aria-hidden />}
      {children}
      {IconRight && <IconRight size={14} aria-hidden className="opacity-70" />}
    </button>
  );
});

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  icon: LucideIcon;
  /** Accessible name and tooltip */
  label: string;
  shortcut?: string;
  size?: "sm" | "md";
  active?: boolean;
  tone?: "default" | "danger" | "brand";
  iconSize?: number;
  badge?: ReactNode;
  /** Hide the tooltip (e.g. when a visible label sits next to it) */
  noTip?: boolean;
}

/** Square icon-only button. 32px on desktop, 44px touch target on phone. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon: Icon, label, shortcut, size = "md", active, tone = "default", iconSize, badge, noTip, className, type = "button", ...rest }, ref,
) {
  return (
    <button ref={ref} type={type} aria-label={label} data-tip={noTip ? undefined : label} data-tip-key={shortcut}
      className={cx("inline-grid shrink-0 place-items-center rounded-control transition-colors duration-150 ease-out disabled:opacity-40", badge ? "relative" : null,
        size === "sm" ? "h-7 w-7 phone:h-11 phone:w-11" : "h-8 w-8 phone:h-11 phone:w-11",
        active ? "bg-brand-soft text-brand-text" : tone === "danger" ? "text-text-muted hover:bg-danger-soft hover:text-danger" : tone === "brand" ? "text-brand-text hover:bg-brand-soft" : "text-text-muted hover:bg-hover hover:text-text active:bg-pressed",
        className)}
      {...rest}>
      <Icon size={iconSize ?? (size === "sm" ? 16 : 18)} aria-hidden />
      {badge}
    </button>
  );
});

export function Kbd({ children, className }: { children: ReactNode; className?: string }) {
  return <kbd className={cx("inline-grid h-5 min-w-5 place-items-center rounded-[4px] border border-border bg-surface-sunken px-1 font-sans text-caption font-medium normal-case tracking-normal text-text-muted", className)}>{children}</kbd>;
}

export function Spinner({ size = 16, className }: { size?: number; className?: string }) {
  return <Loader2 size={size} className={cx("anim-spin text-text-faint", className)} aria-label="Loading" />;
}

// ------------------------------------------------------------------ form fields
export const fieldCls = "w-full rounded-control border border-border bg-surface-raised px-3 text-body text-text outline-none transition-colors duration-150 placeholder:text-text-faint hover:border-border-strong focus:border-brand focus:ring-2 focus:ring-brand/20 disabled:bg-surface-sunken disabled:text-text-muted";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...rest }, ref) {
  return <input ref={ref} className={cx(fieldCls, "h-9 phone:h-11", className)} {...rest} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={cx(fieldCls, "py-2", className)} {...rest} />;
});

/** Label + control + optional hint, stacked. */
export function Field({ label, hint, error, children, htmlFor, className }: { label: string; hint?: ReactNode; error?: string | null; children: ReactNode; htmlFor?: string; className?: string }) {
  return (
    <div className={className}>
      <label htmlFor={htmlFor} className="mb-1.5 block text-meta font-medium text-text-muted">{label}</label>
      {children}
      {(error || hint) && <p className={cx("mt-1.5 text-meta", error ? "text-danger" : "text-text-faint")}>{error ?? hint}</p>}
    </div>
  );
}

/** On/off toggle with a label. */
export function Switch({ checked, onChange, label, description, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; description?: ReactNode; disabled?: boolean }) {
  const id = useId();
  return (
    <div className="flex items-start gap-3">
      <div className="min-w-0 flex-1">
        <label htmlFor={id} className="block text-body text-text">{label}</label>
        {description && <p className="text-meta text-text-faint">{description}</p>}
      </div>
      <button id={id} type="button" role="switch" aria-checked={checked} disabled={disabled} onClick={() => onChange(!checked)}
        className={cx("relative mt-0.5 h-6 w-10 shrink-0 rounded-full transition-colors duration-150 disabled:opacity-50", checked ? "bg-brand-strong" : "bg-border-strong")}>
        <span className={cx("absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-200 ease-out", checked ? "translate-x-[18px]" : "translate-x-0.5")} />
      </button>
    </div>
  );
}

/** Segmented control (tabs-like single choice). */
export function SegmentedControl<T extends string>({ options, value, onChange, label, size = "md", className, iconOnlyOnPhone }: {
  options: { value: T; label: string; icon?: LucideIcon }[]; value: T; onChange: (v: T) => void; label: string; size?: "sm" | "md"; className?: string; iconOnlyOnPhone?: boolean;
}) {
  return (
    <div role="radiogroup" aria-label={label} className={cx("inline-flex rounded-[8px] bg-surface-sunken p-0.5", className)}>
      {options.map((o) => {
        const on = o.value === value;
        const Icon = o.icon;
        return (
          <button key={o.value} type="button" role="radio" aria-checked={on} aria-label={iconOnlyOnPhone ? o.label : undefined} onClick={() => onChange(o.value)}
            className={cx("inline-flex flex-1 items-center justify-center gap-1.5 rounded-control px-2.5 font-medium transition-all duration-150 ease-out",
              size === "sm" ? "h-7 text-meta phone:h-10" : "h-7 text-body phone:h-10",
              on ? "bg-surface-raised text-text shadow-[0_1px_2px_rgb(0_0_0/0.08),0_0_0_1px_var(--color-border)]" : "text-text-muted hover:text-text")}>
            {Icon && <Icon size={15} aria-hidden />}
            <span className={iconOnlyOnPhone ? "phone:sr-only" : undefined}>{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Pill button: a property value in quick add, a filter, a sub-project. */
export const Chip = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & {
  icon?: LucideIcon; iconColor?: string; dot?: string; active?: boolean; onRemove?: () => void; removeLabel?: string; tone?: string;
}>(function Chip({ icon: Icon, iconColor, dot, active, onRemove, removeLabel, tone, className, children, type = "button", ...rest }, ref) {
  return (
    <span className={cx("inline-flex max-w-full items-center rounded-full border text-meta font-medium transition-colors duration-150",
      active ? "border-transparent bg-brand-soft text-brand-text" : "border-border bg-surface-raised text-text-muted hover:border-border-strong hover:text-text", tone, className)}>
      <button ref={ref} type={type} className="inline-flex min-w-0 items-center gap-1.5 py-1 pl-2.5 pr-2.5 phone:min-h-11 phone:px-3.5" {...rest}>
        {dot && <span aria-hidden className="h-2 w-2 shrink-0 rounded-full" style={{ background: dot }} />}
        {Icon && <Icon size={14} aria-hidden style={iconColor ? { color: iconColor } : undefined} />}
        <span className="truncate">{children}</span>
      </button>
      {onRemove && (
        <button type="button" aria-label={removeLabel ?? "Remove"} onClick={onRemove}
          className="-ml-1 mr-1 grid h-5 w-5 place-items-center rounded-full text-text-faint hover:bg-hover hover:text-text phone:-ml-2 phone:mr-0 phone:h-11 phone:w-11">
          <span aria-hidden className="text-[13px] leading-none">×</span>
        </button>
      )}
    </span>
  );
});

// ------------------------------------------------------------------ identity
export function Dot({ color, className = "", size = 10 }: { color: string; className?: string; size?: number }) {
  return <span aria-hidden className={cx("inline-block shrink-0 rounded-full", className)} style={{ background: colorHex(color), width: size, height: size }} />;
}

export function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join("") || "?";
}

export function Avatar({ person, size = 20, className }: { person: Pick<Collaborator, "name" | "image"> | undefined; size?: number; className?: string }) {
  if (!person) return null;
  const hue = [...person.name].reduce((h, ch) => (h * 31 + ch.charCodeAt(0)) % 360, 7);
  return person.image
    ? <img src={person.image} alt={person.name} title={person.name} className={cx("shrink-0 rounded-full object-cover", className)} style={{ width: size, height: size }} />
    : (
      <span title={person.name} role="img" aria-label={person.name} className={cx("inline-grid shrink-0 select-none place-items-center rounded-full font-semibold text-white", className)}
        style={{ width: size, height: size, fontSize: Math.max(9, size * 0.42), background: `hsl(${hue} 45% 46%)` }}>
        {initials(person.name)}
      </span>
    );
}

export function ColorPicker({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  return (
    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Colour">
      {PROJECT_COLORS.map((c) => (
        <button key={c} type="button" role="radio" aria-checked={value === c} aria-label={c.replace(/_/g, " ")} data-tip={c.replace(/_/g, " ")}
          onClick={() => onChange(c)}
          className={cx("grid h-7 w-7 place-items-center rounded-full ring-offset-2 ring-offset-surface-raised transition-transform duration-150 hover:scale-110 phone:h-9 phone:w-9", value === c && "ring-2 ring-text")}
          style={{ background: colorHex(c) }}>
          {value === c && <Check size={14} className="text-white" aria-hidden />}
        </button>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ layout pieces
/** Page title block. Registers the title so the phone top bar can show it once scrolled. */
export function PageHeader({ title, subtitle, actions, leading, below }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; leading?: ReactNode; below?: ReactNode }) {
  const [el, setEl] = useState<HTMLHeadingElement | null>(null);
  useEffect(() => {
    if (!el) return;
    pageTitle.set({ text: el.textContent ?? "", hidden: false });
    const root = document.getElementById("main-scroll");
    if (typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([e]) => pageTitle.set({ text: el.textContent ?? "", hidden: !e!.isIntersecting }), { root, threshold: 0, rootMargin: "-52px 0px 0px 0px" });
    io.observe(el);
    return () => { io.disconnect(); pageTitle.set({ text: "", hidden: false }); };
  }, [el, typeof title === "string" ? title : null]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <header className="mb-6 phone:mb-4">
      <div className="flex min-h-[34px] items-center gap-3">
        {leading}
        <h1 ref={setEl} className="min-w-0 flex-1 truncate text-title font-semibold phone:text-[26px]">{title}</h1>
        {actions && <div className="flex shrink-0 items-center gap-1">{actions}</div>}
      </div>
      {subtitle && <div className="mt-1 text-body text-text-muted">{subtitle}</div>}
      {below}
    </header>
  );
}

/** Tiny store of the current page title for the phone compact bar. */
const titleListeners = new Set<() => void>();
let titleState = { text: "", hidden: false };
export const pageTitle = {
  set(v: { text: string; hidden: boolean }) { if (v.text !== titleState.text || v.hidden !== titleState.hidden) { titleState = v; titleListeners.forEach((l) => l()); } },
  use() { return useSyncExternalStore((cb) => { titleListeners.add(cb); return () => { titleListeners.delete(cb); }; }, () => titleState, () => titleState); },
};

export function SectionHeader({ title, count, actions, tone, className, id, children }: { title: ReactNode; count?: number; actions?: ReactNode; tone?: string; className?: string; id?: string; children?: ReactNode }) {
  return (
    <div className={cx("flex min-h-8 items-center gap-2 border-b border-border pb-1.5", className)}>
      {children}
      <h2 id={id} className={cx("min-w-0 truncate text-heading font-semibold", tone)}>{title}</h2>
      {count !== undefined && count > 0 && <span className="tabular text-meta text-text-faint">{count}</span>}
      {actions && <div className="ml-auto flex items-center gap-1">{actions}</div>}
    </div>
  );
}

export function Card({ title, description, children, actions, className, id }: { title?: ReactNode; description?: ReactNode; children: ReactNode; actions?: ReactNode; className?: string; id?: string }) {
  return (
    <section id={id} className={cx("rounded-card border border-border bg-surface-raised", className)}>
      {(title || actions) && (
        <header className="flex items-start gap-3 px-5 pt-4 phone:px-4">
          <div className="min-w-0 flex-1">
            {title && <h2 className="text-heading font-semibold">{title}</h2>}
            {description && <p className="mt-0.5 text-body text-text-muted">{description}</p>}
          </div>
          {actions}
        </header>
      )}
      <div className="px-5 py-4 phone:px-4">{children}</div>
    </section>
  );
}

/** One row of a properties grid: [icon] [label] [value button]. */
export function PropertyRow({ icon: Icon, label, children, className }: { icon: LucideIcon; label: string; children: ReactNode; className?: string }) {
  return (
    <div className={cx("grid min-h-9 grid-cols-[20px_96px_minmax(0,1fr)] items-center gap-x-2 phone:min-h-11", className)}>
      <Icon size={16} className="text-text-faint" aria-hidden />
      <span className="truncate text-body text-text-muted">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/** The clickable value in a PropertyRow. */
export const PropertyValue = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { empty?: boolean; emptyText?: string }>(
  function PropertyValue({ empty, emptyText = "Add…", className, children, type = "button", ...rest }, ref) {
    return (
      <button ref={ref} type={type} className={cx("-mx-2 flex min-h-8 w-[calc(100%+16px)] min-w-0 items-center gap-2 rounded-control px-2 text-left text-body transition-colors duration-150 hover:bg-hover phone:min-h-11", empty ? "text-text-faint" : "text-text", className)} {...rest}>
        {empty ? emptyText : children}
      </button>
    );
  },
);

export function EmptyState({ kind = "tasks", title, children, action, compact }: { kind?: IllustrationKind; title: string; children?: ReactNode; action?: ReactNode; compact?: boolean }) {
  return (
    <div className={cx("flex flex-col items-center text-center", compact ? "py-8" : "py-14")}>
      <Illustration kind={kind} className={compact ? "h-20 w-28" : "h-28 w-40"} />
      <p className="mt-4 text-heading font-semibold text-text">{title}</p>
      {children && <div className="mt-1 max-w-sm text-body text-text-muted">{children}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/** Turns URLs in plain text into links. */
export function Linkify({ text }: { text: string }) {
  const parts = text.split(/(https?:\/\/[^\s<]+)/g);
  return <>{parts.map((p, i) => (/^https?:\/\//.test(p)
    ? <a key={i} href={p} target="_blank" rel="noreferrer noopener" className="text-info underline decoration-info/40 underline-offset-2 hover:decoration-info" onClick={(e) => e.stopPropagation()}>{p}</a>
    : <span key={i}>{p}</span>))}</>;
}

/** Inline markdown-lite: **bold**, *italic*, `code`, [text](url) and bare links. */
function inline(text: string, key: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|\*[^*\s][^*]*\*|`[^`]+`|\[[^\]]+\]\(https?:\/\/[^)\s]+\)|https?:\/\/[^\s<)]+)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const t = m[0];
    const k = `${key}-${i++}`;
    if (t.startsWith("**")) out.push(<strong key={k} className="font-semibold">{t.slice(2, -2)}</strong>);
    else if (t.startsWith("`")) out.push(<code key={k} className="rounded bg-surface-sunken px-1 font-mono text-[0.92em]">{t.slice(1, -1)}</code>);
    else if (t.startsWith("[")) {
      const [, label, href] = /\[([^\]]+)\]\(([^)]+)\)/.exec(t)!;
      out.push(<a key={k} href={href} target="_blank" rel="noreferrer noopener" className="text-info underline decoration-info/40 underline-offset-2" onClick={(e) => e.stopPropagation()}>{label}</a>);
    } else if (t.startsWith("http")) out.push(<a key={k} href={t} target="_blank" rel="noreferrer noopener" className="break-all text-info underline decoration-info/40 underline-offset-2" onClick={(e) => e.stopPropagation()}>{t}</a>);
    else out.push(<em key={k}>{t.slice(1, -1)}</em>);
    last = m.index + t.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function Markdown({ text, className }: { text: string; className?: string }) {
  const blocks: ReactNode[] = [];
  const lines = text.split("\n");
  let list: { ordered: boolean; items: string[] } | null = null;
  let para: string[] = [];
  const flushPara = () => { if (para.length) { blocks.push(<p key={`p${blocks.length}`}>{inline(para.join("\n"), `p${blocks.length}`)}</p>); para = []; } };
  const flushList = () => {
    if (!list) return;
    const Tag = list.ordered ? "ol" : "ul";
    const k = blocks.length;
    blocks.push(<Tag key={`l${k}`} className={cx("pl-5", list.ordered ? "list-decimal" : "list-disc")}>{list.items.map((it, j) => <li key={j}>{inline(it, `l${k}-${j}`)}</li>)}</Tag>);
    list = null;
  };
  for (const line of lines) {
    const bullet = /^\s*[-*•]\s+(.*)$/.exec(line);
    const num = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (bullet || num) {
      flushPara();
      const ordered = !!num;
      if (list && list.ordered !== ordered) flushList();
      list ??= { ordered, items: [] };
      list.items.push((bullet ?? num)![1]!);
    } else if (!line.trim()) { flushPara(); flushList(); } else { flushList(); para.push(line); }
  }
  flushPara(); flushList();
  return <div className={cx("space-y-2 whitespace-pre-wrap break-words", className)}>{blocks}</div>;
}
